/**
 * Secure Token Handler
 *
 * Industry best practices implementation for token expiration handling
 * and secure logging following OAuth2/JWT security guidelines.
 *
 * Features:
 * - Secure logging without sensitive data exposure
 * - Token rotation support
 * - Anomaly detection
 * - Rate limiting protection
 * - Comprehensive audit trails
 */

import { TOKEN_STORAGE_KEYS } from "../utils/constants";
import {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";

/** Axios request config extended with a one-shot retry marker. */
type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

// Use dependency injection to break circular dependency
// authStore imports apiClient, which imports this file
// By using callbacks/functions instead of direct imports, we break the cycle
type AuthStoreState = {
  user?: { id?: number };
  isAuthenticated: boolean;
  clearAuth: () => void;
  logout: () => Promise<void>;
};

/**
 * Type for API errors that may indicate token expiration
 * Supports both Axios errors and generic error objects
 */
type TokenExpirationError =
  | AxiosError<{
      detail?: string;
      message?: string;
    }>
  | (Error & {
      response?: {
        status?: number;
        data?: {
          detail?: string;
          message?: string;
        };
        headers?: {
          "www-authenticate"?: string;
          "WWW-Authenticate"?: string;
        };
      };
      code?: string;
    });

// Registry pattern: Store auth store getter function
// This will be set after both modules are loaded, breaking the circular dependency
let authStoreGetter: (() => AuthStoreState | null) | null = null;

/**
 * Register auth store getter function
 * Call this after authStore is initialized to break circular dependency
 */
export const registerAuthStoreGetter = (
  getter: () => AuthStoreState | null
): void => {
  authStoreGetter = getter;
};

// Get auth store state (returns null if not registered, breaking the cycle)
const getAuthStoreState = (): AuthStoreState | null => {
  if (!authStoreGetter) {
    // Try lazy import as fallback (only if not registered)
    try {
      // Use dynamic import to avoid circular dependency at module load time
      // This will only work if the module is already loaded
      const module = (
        globalThis as {
          __authStoreModule?: {
            useAuthStore?: { getState: () => AuthStoreState | null };
          };
        }
      ).__authStoreModule;
      if (module?.useAuthStore) {
        return module.useAuthStore.getState();
      }
    } catch {
      // Module not available - this is expected during initial load
    }
    return null;
  }
  return authStoreGetter();
};

// Security logging levels
export enum SecurityLogLevel {
  INFO = "INFO",
  WARN = "WARN",
  ERROR = "ERROR",
  AUDIT = "AUDIT",
}

// Token expiration reasons
export enum TokenExpirationReason {
  EXPIRED = "EXPIRED",
  INVALID = "INVALID",
  REVOKED = "REVOKED",
  MALFORMED = "MALFORMED",
  SUSPICIOUS_ACTIVITY = "SUSPICIOUS_ACTIVITY",
}

// Security event types for audit logging
export enum SecurityEventType {
  TOKEN_EXPIRED = "TOKEN_EXPIRED",
  TOKEN_REFRESHED = "TOKEN_REFRESHED",
  TOKEN_REVOKED = "TOKEN_REVOKED",
  LOGOUT_INITIATED = "LOGOUT_INITIATED",
  SUSPICIOUS_ACTIVITY = "SUSPICIOUS_ACTIVITY",
  RATE_LIMIT_EXCEEDED = "RATE_LIMIT_EXCEEDED",
}

export interface TokenExpirationOptions {
  redirectToLogin?: boolean;
  showNotification?: boolean;
  customCallback?: () => void;
  reason?: TokenExpirationReason;
  attemptRefresh?: boolean;
  rateLimitCheck?: boolean;
  /**
   * Axios instance used to retry the original request after a successful refresh.
   * Required for the standard 401 → refresh → retry flow.
   */
  axiosInstance?: AxiosInstance;
  // Dependency injection to break circular dependency
  getAuthState?: () => {
    user?: { id?: number };
    isAuthenticated: boolean;
    clearAuth: () => void;
    logout: () => Promise<void>;
  } | null;
}

export interface SecurityLogEntry {
  timestamp: string;
  level: SecurityLogLevel;
  eventType: SecurityEventType;
  userId?: string;
  sessionId?: string;
  ipAddress?: string;
  userAgent?: string;
  reason?: string;
  details?: Record<string, unknown>;
  // Never include sensitive data like tokens, passwords, or PII
}

// Rate limiting for token operations
class TokenRateLimiter {
  private attempts: Map<string, { count: number; lastAttempt: number }> =
    new Map();
  private readonly maxAttempts = 5;
  private readonly windowMs = 15 * 60 * 1000; // 15 minutes

  isRateLimited(identifier: string): boolean {
    const now = Date.now();
    const attempt = this.attempts.get(identifier);

    if (!attempt) {
      this.attempts.set(identifier, { count: 1, lastAttempt: now });
      return false;
    }

    // Reset if window has passed
    if (now - attempt.lastAttempt > this.windowMs) {
      this.attempts.set(identifier, { count: 1, lastAttempt: now });
      return false;
    }

    // Check if rate limit exceeded
    if (attempt.count >= this.maxAttempts) {
      return true;
    }

    // Increment counter
    attempt.count++;
    attempt.lastAttempt = now;
    return false;
  }

  reset(identifier: string): void {
    this.attempts.delete(identifier);
  }
}

// Secure logging utility
class SecureLogger {
  private static instance: SecureLogger;
  private logs: SecurityLogEntry[] = [];
  private readonly maxLogs = 1000; // Keep last 1000 entries

  static getInstance(): SecureLogger {
    if (!SecureLogger.instance) {
      SecureLogger.instance = new SecureLogger();
    }
    return SecureLogger.instance;
  }

  log(entry: Omit<SecurityLogEntry, "timestamp">): void {
    const logEntry: SecurityLogEntry = {
      ...entry,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      // Note: IP address would be obtained server-side in production
    };

    // Add to in-memory logs
    this.logs.push(logEntry);

    // Keep only recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Log to console with sanitized data
    this.logToConsole(logEntry);
  }

  private logToConsole(entry: SecurityLogEntry): void {
    const sanitizedEntry = {
      ...entry,
      // Remove any potentially sensitive data
      details: this.sanitizeDetails(entry.details),
    };

    switch (entry.level) {
      case SecurityLogLevel.ERROR:
        console.error("[SECURITY]", sanitizedEntry);
        break;
      case SecurityLogLevel.WARN:
        console.warn("[SECURITY]", sanitizedEntry);
        break;
      case SecurityLogLevel.AUDIT:
        console.info("[AUDIT]", sanitizedEntry);
        break;
      default:
        console.log("[SECURITY]", sanitizedEntry);
    }
  }

  private sanitizeDetails(
    details?: Record<string, unknown>
  ): Record<string, unknown> {
    if (!details) return {};

    const sanitized: Record<string, unknown> = {};
    const sensitiveKeys = [
      "token",
      "password",
      "secret",
      "key",
      "auth",
      "credential",
    ];

    for (const [key, value] of Object.entries(details)) {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
        sanitized[key] = "[REDACTED]";
      } else if (typeof value === "string" && value.length > 100) {
        // Truncate long strings that might contain sensitive data
        sanitized[key] = value.substring(0, 100) + "...";
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  getLogs(): SecurityLogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }
}

// Anomaly detection
class AnomalyDetector {
  private static instance: AnomalyDetector;
  private patterns: Map<string, { count: number; timestamps: number[] }> =
    new Map();

  static getInstance(): AnomalyDetector {
    if (!AnomalyDetector.instance) {
      AnomalyDetector.instance = new AnomalyDetector();
    }
    return AnomalyDetector.instance;
  }

  detectAnomaly(eventType: SecurityEventType, userId?: string): boolean {
    const key = `${eventType}_${userId || "anonymous"}`;
    const now = Date.now();
    const windowMs = 5 * 60 * 1000; // 5 minutes
    const threshold = 10; // Max 10 events per window

    const pattern = this.patterns.get(key) || { count: 0, timestamps: [] };

    // Remove old timestamps
    pattern.timestamps = pattern.timestamps.filter((ts) => now - ts < windowMs);

    // Add current timestamp
    pattern.timestamps.push(now);
    pattern.count = pattern.timestamps.length;

    this.patterns.set(key, pattern);

    // Check if anomaly detected
    if (pattern.count > threshold) {
      SecureLogger.getInstance().log({
        level: SecurityLogLevel.WARN,
        eventType: SecurityEventType.SUSPICIOUS_ACTIVITY,
        userId,
        reason: `High frequency of ${eventType} events detected`,
        details: {
          eventType,
          count: pattern.count,
          threshold,
          windowMs,
        },
      });
      return true;
    }

    return false;
  }
}

// Main token handler with industry best practices
export class SecureTokenHandler {
  private static instance: SecureTokenHandler;
  private rateLimiter = new TokenRateLimiter();
  private logger = SecureLogger.getInstance();
  private anomalyDetector = AnomalyDetector.getInstance();
  /** In-flight refresh shared across concurrent 401s (single-flight). */
  private refreshPromise: Promise<boolean> | null = null;

  static getInstance(): SecureTokenHandler {
    if (!SecureTokenHandler.instance) {
      SecureTokenHandler.instance = new SecureTokenHandler();
    }
    return SecureTokenHandler.instance;
  }

  /**
   * Refresh access token once; concurrent callers await the same promise.
   */
  async refreshWithSingleFlight(): Promise<boolean> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.attemptTokenRefresh().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  /**
   * Handles token expiration with comprehensive security measures
   */
  async handleTokenExpiration(
    error: TokenExpirationError,
    options: TokenExpirationOptions = {}
  ): Promise<void> {
    const {
      redirectToLogin = true,
      showNotification = true,
      customCallback,
      reason = TokenExpirationReason.EXPIRED,
      attemptRefresh = false,
      rateLimitCheck = true,
    } = options;

    // Extract user info for logging (without sensitive data)
    // Use dependency injection to avoid circular dependency
    const authStore = options.getAuthState?.() || getAuthStoreState();
    const userId = authStore?.user?.id?.toString();
    const sessionId = this.generateSessionId();

    // Rate limiting gates toast/redirect spam only — never skip session clear.
    let suppressUserFacingSpam = false;
    if (rateLimitCheck && userId) {
      const identifier = `token_expiration_${userId}`;
      if (this.rateLimiter.isRateLimited(identifier)) {
        suppressUserFacingSpam = true;
        this.logger.log({
          level: SecurityLogLevel.WARN,
          eventType: SecurityEventType.RATE_LIMIT_EXCEEDED,
          userId,
          reason: "Token expiration handling rate limited",
          details: { identifier },
        });
      }
    }

    // Anomaly detection
    this.anomalyDetector.detectAnomaly(SecurityEventType.TOKEN_EXPIRED, userId);

    // Log the security event
    this.logger.log({
      level: SecurityLogLevel.AUDIT,
      eventType: SecurityEventType.TOKEN_EXPIRED,
      userId,
      sessionId,
      reason: `Token expiration: ${reason}`,
      details: {
        errorStatus: error?.response?.status,
        errorCode: error?.code,
        attemptRefresh,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
    });

    try {
      // Attempt token refresh if enabled and possible
      if (attemptRefresh && (await this.attemptTokenRefresh())) {
        this.logger.log({
          level: SecurityLogLevel.INFO,
          eventType: SecurityEventType.TOKEN_REFRESHED,
          userId,
          sessionId,
          reason: "Token successfully refreshed",
        });
        return;
      }

      // Clear auth state securely (always — including when rate-limited)
      await this.performSecureLogout(userId, sessionId);

      // Execute custom callback if provided
      if (customCallback) {
        try {
          customCallback();
        } catch (callbackError) {
          this.logger.log({
            level: SecurityLogLevel.ERROR,
            eventType: SecurityEventType.TOKEN_EXPIRED,
            userId,
            sessionId,
            reason: "Custom callback failed during token expiration",
            details: { error: callbackError },
          });
        }
      }

      // Show user notification (rate-limited to avoid toast spam)
      if (showNotification && !suppressUserFacingSpam) {
        this.showSecureNotification(
          "Your session has expired. Please log in again."
        );
      }

      // Redirect to login if enabled and user is authenticated
      if (
        redirectToLogin &&
        !suppressUserFacingSpam &&
        authStore?.isAuthenticated &&
        window.location.pathname !== "/login"
      ) {
        this.logger.log({
          level: SecurityLogLevel.INFO,
          eventType: SecurityEventType.LOGOUT_INITIATED,
          userId,
          sessionId,
          reason: "Redirecting to login due to token expiration",
        });

        // Use replaceState to prevent back button issues
        window.history.replaceState(null, "", "/login");
        window.location.href = "/login";
      }
    } catch (error) {
      this.logger.log({
        level: SecurityLogLevel.ERROR,
        eventType: SecurityEventType.TOKEN_EXPIRED,
        userId,
        sessionId,
        reason: "Error during token expiration handling",
        details: {
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });

      // Fallback cleanup
      await this.performFallbackCleanup();
    }
  }

  /**
   * Attempts to refresh the token using httpOnly cookie refresh token
   */
  private async attemptTokenRefresh(): Promise<boolean> {
    try {
      // Use refresh endpoint - refresh token is in httpOnly cookie
      const { authAPI } = await import("../services/authApi");
      await authAPI.refresh();

      this.logger.log({
        level: SecurityLogLevel.INFO,
        eventType: SecurityEventType.TOKEN_REFRESHED,
        reason: "Token successfully refreshed via httpOnly cookie",
      });
      return true;
    } catch (error) {
      this.logger.log({
        level: SecurityLogLevel.WARN,
        eventType: SecurityEventType.TOKEN_EXPIRED,
        reason: "Token refresh failed",
        details: {
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });
      return false;
    }
  }

  /**
   * Performs secure logout with comprehensive cleanup
   */
  private async performSecureLogout(
    userId?: string,
    sessionId?: string
  ): Promise<void> {
    try {
      // Use dependency injection to avoid circular dependency
      const authStore = getAuthStoreState();

      // Log logout initiation
      this.logger.log({
        level: SecurityLogLevel.AUDIT,
        eventType: SecurityEventType.LOGOUT_INITIATED,
        userId,
        sessionId,
        reason: "Secure logout initiated",
      });

      // Clear auth state
      if (authStore) {
        authStore.clearAuth();
        // Call server-side logout
        await authStore.logout();
      }

      // Clear rate limiter for this user
      if (userId) {
        this.rateLimiter.reset(`token_expiration_${userId}`);
      }
    } catch (error) {
      this.logger.log({
        level: SecurityLogLevel.ERROR,
        eventType: SecurityEventType.LOGOUT_INITIATED,
        userId,
        sessionId,
        reason: "Error during secure logout",
        details: {
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });
      throw error;
    }
  }

  /**
   * Fallback cleanup when main logout fails
   */
  private async performFallbackCleanup(): Promise<void> {
    try {
      // SECURITY: Tokens are in httpOnly cookies, not localStorage
      // Only clear auth store state - cookies are cleared by backend on logout
      // Clear any legacy localStorage tokens for migration
      localStorage.removeItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(TOKEN_STORAGE_KEYS.TOKEN);
      localStorage.removeItem(TOKEN_STORAGE_KEYS.AUTH_TOKEN);
      // Selective auth/CSRF keys only — never sessionStorage.clear()
      const { clearAuthSessionStorage } = await import(
        "./clearAuthSessionStorage"
      );
      clearAuthSessionStorage();

      // Clear auth store state (use dependency injection to avoid circular dependency)
      const authStore = getAuthStoreState();
      authStore?.clearAuth();

      this.logger.log({
        level: SecurityLogLevel.WARN,
        eventType: SecurityEventType.LOGOUT_INITIATED,
        reason: "Fallback cleanup performed",
      });
    } catch (error) {
      this.logger.log({
        level: SecurityLogLevel.ERROR,
        eventType: SecurityEventType.LOGOUT_INITIATED,
        reason: "Fallback cleanup failed",
        details: {
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });
    }
  }

  /**
   * Shows secure notification to user
   * Uses toast notifications for better UX (non-blocking, accessible, styled)
   */
  private showSecureNotification(message: string): void {
    // Only show notification if user is actually authenticated
    // Use dependency injection to avoid circular dependency
    const authStore = getAuthStoreState();
    if (!authStore?.isAuthenticated) {
      return; // Don't show session expired message for non-authenticated users
    }

    // Use toast notification (react-hot-toast is already in dependencies)
    // Lazy import to avoid circular dependencies
    import("react-hot-toast")
      .then(({ default: toast }) => {
        toast.error(message, {
          duration: 5000,
          position: "top-center",
          id: "token-expired", // Prevent duplicate notifications
          ariaProps: {
            role: "alert",
            "aria-live": "assertive",
          },
        });
      })
      .catch(() => {
        // Fallback to console if toast is unavailable (should not happen)
        if (typeof window !== "undefined" && window.console) {
          console.error("[SECURITY] Token expiration notification:", message);
        }
      });
  }

  /**
   * Generates a session ID for tracking
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Checks if an error indicates token expiration
   */
  isTokenExpired(error: TokenExpirationError): boolean {
    const status = error?.response?.status;
    const data = error?.response?.data || {};
    const headerAuth =
      error?.response?.headers?.["www-authenticate"] ||
      error?.response?.headers?.["WWW-Authenticate"];

    const detailRaw = (data.detail ?? data.message ?? "").toString();
    const errorMessageRaw = (error?.message ?? "").toString();

    const detail = detailRaw.toLowerCase();
    const errorMessage = errorMessageRaw.toLowerCase();
    const authHeader =
      typeof headerAuth === "string" ? headerAuth.toLowerCase() : "";

    // Known backend auth messages that indicate token problems
    const tokenPhrases = [
      "token has expired",
      "could not validate credentials",
      "invalid or expired refresh token",
      "invalid token type",
      "invalid token",
      "token expired",
      "token malformed",
      "not authenticated",
    ];

    const hasTokenPhrase =
      tokenPhrases.some((phrase) => detail.includes(phrase)) ||
      tokenPhrases.some((phrase) => errorMessage.includes(phrase));

    // Treat 401 as token-related only when:
    // - WWW-Authenticate header suggests Bearer auth, or
    // - Response/detail clearly references token-related phrases
    if (status === 401) {
      const isBearerAuth = authHeader.includes("bearer");
      return isBearerAuth || hasTokenPhrase;
    }

    // Treat 403 as token-related only when detail/message clearly mentions token issues
    if (status === 403 && hasTokenPhrase) {
      return true;
    }

    // Fallback: explicit token wording even without clear status
    return (
      detail.includes("token expired") ||
      detail.includes("token has expired") ||
      errorMessage.includes("token expired") ||
      errorMessage.includes("token has expired")
    );
  }

  /**
   * Gets security logs (for debugging/admin purposes)
   */
  getSecurityLogs(): SecurityLogEntry[] {
    return this.logger.getLogs();
  }

  /**
   * Clears security logs
   */
  clearSecurityLogs(): void {
    this.logger.clearLogs();
  }
}

// Export singleton instance and utility functions
export const secureTokenHandler = SecureTokenHandler.getInstance();

// Legacy compatibility functions
export const handleTokenExpiration = async (
  options: TokenExpirationOptions = {}
): Promise<void> => {
  // Create a minimal error object for legacy compatibility
  const mockError: TokenExpirationError = new Error(
    "Token expiration"
  ) as TokenExpirationError;
  await secureTokenHandler.handleTokenExpiration(mockError, options);
};

export const isTokenExpired = (error: TokenExpirationError): boolean => {
  return secureTokenHandler.isTokenExpired(error);
};

export const createTokenExpirationInterceptor = (
  options: TokenExpirationOptions = {}
) => {
  return async (error: TokenExpirationError) => {
    // Only handle token expiration for authenticated requests
    // Check if this is a 401/403 error and if the user is actually authenticated
    // Use dependency injection to avoid circular dependency
    const authStore = options.getAuthState?.() || getAuthStoreState();
    const isAuthenticated = authStore?.isAuthenticated || false;

    // During self-serve provider registration, step-3 profile/template fetches can
    // race the post-step1 login cookie; do not force a global logout loop here.
    const onRegistrationWizard =
      typeof window !== "undefined" &&
      (window.location?.pathname?.startsWith("/provider-registration") ??
        false);

    if (
      !(
        secureTokenHandler.isTokenExpired(error) &&
        isAuthenticated &&
        !onRegistrationWizard
      )
    ) {
      return Promise.reject(error);
    }

    const shouldAttemptRefresh = error?.response?.status === 401;
    const originalRequest = (error as AxiosError).config as
      | RetriableRequestConfig
      | undefined;
    const axiosInstance = options.axiosInstance;

    // Never refresh-on-refresh: a 401 from /auth/refresh must reject to the
    // caller (attemptTokenRefresh) or single-flight can deadlock on itself.
    const requestUrl = originalRequest?.url ?? "";
    if (requestUrl.includes("/auth/refresh")) {
      return Promise.reject(error);
    }

    // Standard Axios pattern: 401 → single-flight refresh → retry once.
    // `_retry` prevents infinite loops if the retried request also 401s.
    if (
      shouldAttemptRefresh &&
      originalRequest &&
      !originalRequest._retry &&
      axiosInstance
    ) {
      originalRequest._retry = true;

      const refreshed = await secureTokenHandler.refreshWithSingleFlight();
      if (refreshed) {
        return axiosInstance.request(originalRequest);
      }

      // Refresh failed — logout / redirect path (do not attempt refresh again)
      await secureTokenHandler.handleTokenExpiration(error, {
        ...options,
        attemptRefresh: false,
      });
      return Promise.reject(error);
    }

    // 403, already retried, or missing retry wiring — existing expiration handling
    await secureTokenHandler.handleTokenExpiration(error, {
      ...options,
      attemptRefresh:
        shouldAttemptRefresh &&
        !originalRequest?._retry &&
        !axiosInstance,
    });

    return Promise.reject(error);
  };
};

export const shouldLogoutUser = (error: TokenExpirationError): boolean => {
  return secureTokenHandler.isTokenExpired(error);
};

export default secureTokenHandler;
